// src/hooks/useCurrentUser.ts — SCE Issues
// Source unique de vérité pour l'utilisateur courant + son rôle métier.
//
// Détection du rôle :
//  - UPN contient '#EXT#'                  → 'fournisseur' (guest B2B Azure AD)
//  - UPN figure dans coordinateurs.config  → 'coordinateur'
//  - sinon                                 → 'operateur'
//
// Le rôle se lit sur la PERSONNE derrière le compte : un compte secondaire
// (téléphone perso d'un salarié, config/comptesAlias.config) prend l'UPN, le
// rôle et le nom de la personne interne qu'il représente — sans quoi son
// `#EXT#` en ferait un fournisseur (cf. auth/identiteCompte).
//
// ─── Bascule de rôle (Lot 7, administrateur en production) ───
//
// Si une vue est active (features/vue-role), ce hook retourne le profil
// choisi À LA PLACE du vrai compte MSAL. Tout le reste de l'app (droits,
// « Mes déclarations », tableau de bord…) voit donc la personne affichée.
//
// ⚠️ Seul l'AFFICHAGE change. Les écritures partent avec le jeton MSAL réel
//    (colonne Author = vrai compte). Quand un texte écrit doit nommer
//    quelqu'un, utiliser `nomReel`, jamais `user.nom`. Qui A LE DROIT de
//    basculer se décide sur le compte réel, dans le contexte — pas ici.

import { useMemo } from 'react'
import { useAuth } from '@/auth/useAuth'
import { identiteDuCompte } from '@/auth/identiteCompte'
import { isCoordinateur } from '@/config/coordinateurs.config'
import { useVueRole } from '@/features/vue-role/contexte'
import { nomComplet, prenom } from '@/utils/noms'

export type UserRole = 'operateur' | 'coordinateur' | 'fournisseur'

export interface CurrentUser {
  upn: string
  /** « Prénom Nom » — l'annuaire renvoie « Nom, Prénom » (utils/noms). */
  nom: string
  /** Le prénom seul, pour simplifier (« Bonjour Guillaume »). */
  prenom: string
  email: string
  role: UserRole
}

export interface UseCurrentUserReturn {
  user: CurrentUser | null
  loading: boolean
  /** Helpers de garde — évite de répéter `user?.role === 'X'` partout */
  isFournisseur: boolean
  isCoordinateur: boolean
  isOperateur: boolean
  /** Une vue d'un autre profil est active (bascule de rôle). */
  enVueRole: boolean
  /**
   * Nom du compte MSAL RÉEL — celui qui signe les écritures. À utiliser dans
   * tout texte écrit dans SharePoint, jamais `user.nom` (qui peut être la
   * personne affichée).
   */
  nomReel: string
}

/**
 * Détecte le rôle utilisateur à partir de l'UPN Azure AD.
 *
 * Convention Microsoft : un guest B2B a un UPN de la forme
 *   user_externaldomain.com#EXT#@sartorius.onmicrosoft.com
 * → la présence de '#EXT#' est le marqueur fiable.
 */
function detectRole(upn: string): UserRole {
  if (!upn) return 'operateur'
  if (upn.includes('#EXT#')) return 'fournisseur'
  if (isCoordinateur(upn)) return 'coordinateur'
  return 'operateur'
}

export function useCurrentUser(): UseCurrentUserReturn {
  const { account, isReady } = useAuth()
  const vueRole = useVueRole()
  // On expose `loading` au reste de l'app, calculé depuis `isReady` MSAL.
  const loading = !isReady

  const user = useMemo<CurrentUser | null>(() => {
    // ─── Bascule de rôle ───
    // Le contexte ne rend un profil QUE si le compte réel y a droit.
    if (vueRole.profil) {
      const p = vueRole.profil
      return {
        upn: p.upn,
        nom: nomComplet(p.nom),
        prenom: prenom(p.nom),
        email: p.email,
        role: p.role,
      }
    }

    // ─── Mode normal : la personne derrière le compte MSAL ───
    const identite = identiteDuCompte(account)
    if (!identite) return null

    return {
      upn: identite.upn,
      nom: nomComplet(identite.nomBrut, identite.upn),
      prenom: prenom(identite.nomBrut, identite.upn),
      email: identite.email,
      role: detectRole(identite.upn),
    }
  }, [account, vueRole.profil])

  return {
    user,
    loading,
    isFournisseur: user?.role === 'fournisseur',
    isCoordinateur: user?.role === 'coordinateur',
    isOperateur: user?.role === 'operateur',
    enVueRole: vueRole.profil !== null,
    nomReel: nomComplet(identiteDuCompte(account)?.nomBrut ?? '', identiteDuCompte(account)?.upn),
  }
}
